// Taşeron portalı — İzne personel atama (Modal + çoklu seçim). api-contract §21.2
// (`POST /supplier/permits/{uuid}/personnel {supplierUserUuids}`). Seçenekler Yetkili'nin **kendi
// contractor'ının** personelinden (§13.6 `GET /supplier/contractor/personnel`) doldurulur; adaylar
// `isPermitCandidate`: `Active` ya da `NoLogin`, her zaman `isActive === true` — pasif kişi asla
// listelenmez; az önce oluşturulan kişi liste yenilemesi gelmeden listeye katılır ve seçilir.
// Zaten kapsanan personel çıkarılır. İdempotent (BE zaten kapsanan personeli atlar). Personel kendi
// contractor'ına + firmaya atanmış değilse → `404 PERSONNEL_NOT_FOUND` (kısmi yazma yok). Firma/
// contractor kimliği istemciden GÖNDERİLMEZ — sunucu bağdan çözer. Atama sonrası izin detayı +
// liste invalidate (readiness güncellenir).

import {
  Alert,
  Button,
  EmptyState,
  Modal,
  PersonnelCreateSheet,
  Spinner,
  Tag,
  isNotActivated,
  resolveApiErrorMessage,
  supplierContractorApi,
  supplierPermitApi,
  useAuthStore,
  usePermitCandidateOptions,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { type RefObject, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePersonnelActivationClient } from '../../lib/personnelActivationClient';
import { supplierContractorKeys } from '../../lib/supplierContractorKeys';
import { supplierPermitKeys } from '../../lib/supplierPermitKeys';

const NOT_ACTIVATED_HINT_ID = 'permit-personnel-not-activated-hint';

interface PermitPersonnelAddModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  permitUuid: string;
  /** Zaten kapsanan personel public kimlikleri — seçenekten çıkarılır. */
  coveredUuids: string[];
  /** Panelin "Personel ekle" düğmesi: modal kapanınca odak buraya döner (R1). */
  restoreFocusRef: RefObject<HTMLElement | null>;
}

export function PermitPersonnelAddModal({
  open,
  onOpenChange,
  permitUuid,
  coveredUuids,
  restoreFocusRef,
}: PermitPersonnelAddModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const companyId = useAuthStore((s) => s.principal?.companyId ?? null);
  const activation = usePersonnelActivationClient(companyId);
  const [creating, setCreating] = useState(false);

  // Kaynak havuz: kendi contractor'ının personeli (§13.6). Adaylar `isPermitCandidate` (GL6 §4.8): aktif
  // Personnel, `Active` ya da `NoLogin`; pasif kişi ve bekleyen davet (henüz SupplierUser'ı yok) atanamaz.
  const personnelQuery = useQuery({
    queryKey: supplierContractorKeys.personnel(companyId),
    queryFn: () => supplierContractorApi.listSupplierContractorPersonnel(),
    enabled: open,
    staleTime: 60_000,
  });

  const {
    options,
    selected,
    visibleSelected,
    toggle,
    checkboxRef,
    createTriggerRef,
    sheetRestoreRef,
    handleCreated,
    reset,
  } = usePermitCandidateOptions(personnelQuery.data, coveredUuids);

  const mutation = useMutation({
    mutationFn: () =>
      supplierPermitApi.assignSupplierPermitPersonnel(permitUuid, {
        supplierUserUuids: visibleSelected,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: supplierPermitKeys.detail(companyId, permitUuid),
      });
      queryClient.invalidateQueries({ queryKey: supplierPermitKeys.list(companyId) });
      resetLocal();
      onOpenChange(false);
    },
  });

  function resetLocal() {
    reset();
    setCreating(false);
  }
  // Modalın kendi kapanışı ve "Vazgeç" aynı yoldan geçer: yerel durum + hata sıfırlanır.
  function close() {
    mutation.reset();
    resetLocal();
    onOpenChange(false);
  }

  const noOptions = personnelQuery.isSuccess && options.length === 0;
  const createButton = (
    <Button
      ref={createTriggerRef}
      variant={noOptions ? 'secondary' : 'ghost'}
      size="sm"
      onClick={() => setCreating(true)}
      disabled={mutation.isPending}
    >
      <UserPlus className="h-4 w-4" aria-hidden="true" />
      {t('permits.personnel.add.createNew')}
    </Button>
  );

  return (
    <>
      <Modal
        open={open && !creating}
        onOpenChange={(next) => {
          if (next) {
            onOpenChange(true);
          } else {
            close();
          }
        }}
        restoreFocusRef={restoreFocusRef}
        title={t('permits.personnel.add.title')}
        description={t('permits.personnel.add.description')}
        size="md"
        closeLabel={t('common.close')}
        footer={
          <>
            <Button variant="secondary" onClick={close} disabled={mutation.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => mutation.mutate()}
              loading={mutation.isPending}
              disabled={visibleSelected.length === 0}
            >
              {mutation.isPending ? (
                <Spinner label={t('permits.personnel.add.submit')} />
              ) : (
                t('permits.personnel.add.submit')
              )}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {mutation.isError ? (
            <Alert tone="danger">{resolveApiErrorMessage(mutation.error, t)}</Alert>
          ) : null}

          {personnelQuery.isPending ? (
            <Spinner label={t('common.loading')} />
          ) : personnelQuery.isError ? (
            <Alert tone="danger">{resolveApiErrorMessage(personnelQuery.error, t)}</Alert>
          ) : noOptions ? (
            <EmptyState
              variant="panel"
              title={t('permits.personnel.add.empty')}
              action={createButton}
            />
          ) : (
            <>
              <div className="flex max-h-72 flex-col gap-1 overflow-y-auto rounded-md border border-border p-2">
                {options.map((p) => {
                  const name = `${p.firstName} ${p.lastName}`.trim();
                  return (
                    <label
                      key={p.uuid}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm text-fg hover:bg-surface-muted"
                    >
                      <input
                        ref={checkboxRef(p.uuid)}
                        type="checkbox"
                        className="h-4 w-4 shrink-0 accent-primary"
                        checked={selected.has(p.uuid)}
                        onChange={() => toggle(p.uuid)}
                        aria-describedby={isNotActivated(p) ? NOT_ACTIVATED_HINT_ID : undefined}
                      />
                      <span
                        className="min-w-0 max-w-[55%] shrink-0 truncate font-medium"
                        title={name}
                      >
                        {name}
                      </span>
                      {p.emailOrPhone ? (
                        <span className="min-w-0 truncate text-muted" title={p.emailOrPhone}>
                          · {p.emailOrPhone}
                        </span>
                      ) : null}
                      {isNotActivated(p) ? (
                        <span className="shrink-0">
                          <Tag color="gray">{t('permits.personnel.notActivated')}</Tag>
                        </span>
                      ) : null}
                    </label>
                  );
                })}
              </div>
              {options.some((p) => isNotActivated(p)) ? (
                <p id={NOT_ACTIVATED_HINT_ID} className="text-xs text-muted">
                  {t('permits.personnel.notActivated.hint')}
                </p>
              ) : null}
              <div>{createButton}</div>
            </>
          )}
        </div>
      </Modal>

      <PersonnelCreateSheet
        open={creating}
        onOpenChange={setCreating}
        client={activation}
        onCreated={handleCreated}
        restoreFocusRef={sheetRestoreRef}
      />
    </>
  );
}
